// src/components/BecomeAVendorAccordion.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabaseClient';
import termsText from '../data/terms';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

import AIAboutGenerator from './AIAboutGenerator';
const countryCodes = [
  { name: 'United States', code: '+1' },
  { name: 'India', code: '+91' },
  { name: 'United Kingdom', code: '+44' },
  // …etc.
];



const initialFormState = {
  business_name: '',
  service_categories: '',
  contact_person_full_name: '',
  email: '',
  phone_country_code: '+1',
  phone_number: '',
  whatsapp_country_code: '+1',
  whatsapp_number: '',
  service_description: '',
  location_filter_state: '',
  service_providing_locations: [],
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip_code: '',
  country: '',
  website_url: '',
  facebook_link: '',
  instagram_link: '',
  portfolio_link: '',
  about_us: '',
  languages_spoken: '',
  hourly_rate: '',
  flat_rate: '',
  availability_notes: '',
  display_image_url: '',
  terms_accepted: false,
};

export default function BecomeAVendorAccordion() {
  const [form, setForm] = useState(initialFormState);
  const [categoriesList, setCategoriesList] = useState([]);
  const [locationsList, setLocationsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [showTerms, setShowTerms] = useState(false);
  const [showCategoryInfo, setShowCategoryInfo] = useState(false);
  const [errors, setErrors] = useState({});
  
useEffect(() => {
    if (form.service_categories && form.state) {
      localStorage.setItem('category', form.service_categories);
      localStorage.setItem('location', form.state);
    }
  }, [form.service_categories, form.state]);


  // Fetch categories with descriptions
  useEffect(() => {
    supabase
      .from('categories')
      .select('id, categories, Description')
      .order('id', { ascending: true })
      .then(({ data, error }) => {
      	console.log('🔍 categories data:', data, 'error:', error);
        if (data) setCategoriesList(data);
      });
  }, []);

  // Fetch locations
  useEffect(() => {
    supabase
      .from('locations')
      .select('name, state')
      .order('state', { ascending: true })
      .then(({ data }) => {
        if (data) setLocationsList(data);
      });
  }, []);

  // Unique states
  const statesList = useMemo(() => {
    const s = new Set(locationsList.map(l => l.state));
    return Array.from(s).sort();
  }, [locationsList]);

  // Handle input changes
  const handleChange = e => {
    const { name, type, value, checked } = e.target;
    setForm(f => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (name === 'location_filter_state') {
      setForm(f => ({ ...f, service_providing_locations: [] }));
    }
  };

  // Handle location checkbox
  const handleLocationCheckboxChange = e => {
    const { value, checked } = e.target;
    setForm(f => {
      const curr = f.service_providing_locations;
      return {
        ...f,
        service_providing_locations: checked
          ? [...curr, value]
          : curr.filter(v => v !== value)
      };
    });
  };

  // File upload
  const handleFileUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;
    const key = `${Date.now()}_${file.name}`;
    setLoading(true);

    const { error: uploadError } = await supabase
      .storage
      .from('vendor-images')
      .upload(key, file);

    if (uploadError) {
      alert('Upload error: ' + uploadError.message);
      setLoading(false);
      return;
    }

    const { data: urlData, error: urlError } = supabase
      .storage
      .from('vendor-images')
      .getPublicUrl(key);

    if (urlError) {
      alert('Could not retrieve image URL');
    } else {
      setForm(f => ({ ...f, display_image_url: urlData.publicUrl }));
    }

    setLoading(false);
  };

  // Validate sections
  const validateSection = sec => {
    const errs = {};
    if (sec === 0) {
      if (!form.business_name) errs.business_name = 'Required';
      if (!form.service_categories) errs.service_categories = 'Select one';
    }
    if (sec === 1) {
      if (!form.contact_person_full_name) errs.contact_person_full_name = 'Required';
      if (!form.email) errs.email = 'Required';
      if (!form.phone_number) errs.phone_number = 'Required';
      if (!form.whatsapp_number) errs.whatsapp_number = 'Required';
    }
    if (sec === 2) {
      if (!form.service_description) errs.service_description = 'Required';
      if (!form.location_filter_state) errs.location_filter_state = 'Select a state';
      if (form.service_providing_locations.length === 0)
        errs.service_providing_locations = 'Select at least one city';
    }
    if (sec === 3) {
      if (!form.address_line1) errs.address_line1 = 'Required';
      if (!form.city) errs.city = 'Required';
      if (!form.state) errs.state = 'Required';
      if (!form.zip_code) errs.zip_code = 'Required';
      if (!form.country) errs.country = 'Required';
    }
    if (sec === 4) {
      if (!form.about_us) errs.about_us = 'Required';
    }
    if (sec === 5) {
      if (!form.display_image_url) errs.display_image_url = 'Upload one';
    }
    if (sec === 6) {
      if (!form.terms_accepted) errs.terms_accepted = 'You must accept terms';
    }
    return errs;
  };

  const sections = [
    'Basic Info',
    'Contact Info',
    'Business Details',
    'Address',
    'Additional Details',
    'Upload Image',
    'Terms & Submit'
  ];


  const handleBack = () => {
    setErrors({});
    setActiveSection(s => Math.max(s - 1, 0));
  };

  // Submit
  const handleSubmit = async e => {
    e.preventDefault();
    const errs = validateSection(activeSection);
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setLoading(true);

    const {
      phone_country_code,
      phone_number,
      whatsapp_country_code,
      whatsapp_number,
      service_providing_locations,
      location_filter_state,
      hourly_rate,
      flat_rate,
      ...rest
    } = form;
  const combinedPhone = (phone_country_code || '') + (phone_number || '');
  const combinedWhatsapp = (whatsapp_country_code || '') + (whatsapp_number || '');

    const payload = {
      ...rest,
      phone_number: combinedPhone,
      whatsapp_number: combinedWhatsapp,
      location_filter_state,
      service_providing_locations: service_providing_locations.join(','),
      display_image_url: form.display_image_url,
      hourly_rate: hourly_rate ? parseFloat(hourly_rate) : null,
      flat_rate: flat_rate ? parseFloat(flat_rate) : null,
    };

    const { error } = await supabase.from('vendors').insert([payload]);
    setLoading(false);

    if (error) {
      alert('Error submitting form: ' + error.message);
    } else {
      alert('Vendor onboarded successfully!');
      setForm(initialFormState);
      setErrors({});
      setActiveSection(0);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">

        {/* Illustration */}
        <div>
          <img
            src="/assets/onboard-illustration.png"
            alt="Onboard Your Business illustration"
            className="w-full h-auto rounded"
          />
        </div>

        {/* Accordion Form */}
        <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-lg shadow p-6">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-2xl font-bold">Onboard Your Business</h2>
            <p className="mt-2 text-gray-600">
              Submit this form to onboard your business with us. We’ll review and get you live quickly!
            </p>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 h-1 rounded-full mb-6">
            <div
              className="bg-green-500 h-1 rounded-full"
              style={{ width: `${((activeSection + 1) / sections.length) * 100}%` }}
            />
          </div>

          {sections.map((title, idx) => (
            <div key={idx} className="border rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setActiveSection(activeSection === idx ? -1 : idx)}
                className={`w-full flex justify-between items-center px-4 py-2 ${
                  activeSection === idx
                    ? 'bg-green-50 border-l-4 border-green-500'
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                <span className="font-semibold">{title}</span>
                {activeSection === idx ? (
                  <ChevronUpIcon className="w-5 h-5" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5" />
                )}
              </button>

              {activeSection === idx && (
                <div className="p-4 space-y-4">

                  {/* Section 0: Basic Info */}
                  {idx === 0 && (
                    <>
                      <div>
                        <label className="block font-medium">Business Name</label>
                        <input
                          name="business_name"
                          value={form.business_name}
                          onChange={handleChange}
                          className="mt-1 w-full border p-2 rounded"
                        />
                        {errors.business_name && (
                          <p className="text-red-500 text-sm">{errors.business_name}</p>
                        )}
                      </div>
                      <div>
                        <label className="block font-medium flex items-center">
                          Service Category
                          <InformationCircleIcon
                            className="ml-2 w-5 h-5 text-gray-400 hover:text-gray-600 cursor-pointer"
                            onClick={() => setShowCategoryInfo(true)}
                          />
                        </label>
                        <select
                          name="service_categories"
                          value={form.service_categories}
                          onChange={handleChange}
                          className="mt-1 w-full border p-2 rounded"
                        >
                          <option value="">Select one</option>
                          {categoriesList.map(cat => (
                            <option key={cat.id} value={cat.categories}>
                              {cat.categories}
                            </option>
                          ))}
                        </select>
                        {errors.service_categories && (
                          <p className="text-red-500 text-sm">{errors.service_categories}</p>
                        )}
                      </div>
                    </>
                  )}

                  {/* Section 1: Contact Info */}
                  {idx === 1 && (
                    <>
                      <div>
                        <label className="block font-medium">Contact Person Name</label>
                        <input
                          name="contact_person_full_name"
                          value={form.contact_person_full_name}
                          onChange={handleChange}
                          className="mt-1 w-full border p-2 rounded"
                        />
                        {errors.contact_person_full_name && (
                          <p className="text-red-500 text-sm">{errors.contact_person_full_name}</p>
                        )}
                      </div>
                      <div>
                        <label className="block font-medium">Email</label>
                        <input
                          name="email"
                          type="email"
                          value={form.email}
                          onChange={handleChange}
                          className="mt-1 w-full border p-2 rounded"
                        />
                        {errors.email && (
                          <p className="text-red-500 text-sm">{errors.email}</p>
                        )}
                      </div>
                      <label className="block font-medium">Phone Number</label>
                      <div className="flex space-x-2">
                        <select
                          name="phone_country_code"
                          value={form.phone_country_code}
                          onChange={handleChange}
                          className="border p-2 rounded-l"
                        >  
                          {countryCodes.map(c => (
                            <option key={c.code} value={c.code}>{`${c.name} (${c.code})`}</option>
                          ))}
                        </select>
                        <input
                          name="phone_number"
                          value={form.phone_number}
                          onChange={handleChange}
                          className="flex-1 border p-2 rounded-r"
                        />
                      </div>
                      {errors.phone_number && (
                        <p className="text-red-500 text-sm">{errors.phone_number}</p>
                      )}
                     <label className="block font-medium">WhatsApp Number</label>
                      <div className="flex space-x-2">
                        <select
                          name="whatsapp_country_code"
                          value={form.whatsapp_country_code}
                          onChange={handleChange}
                          className="border p-2 rounded-l"
                        >
                          {countryCodes.map(c => (
                            <option key={c.code} value={c.code}>{`${c.name} (${c.code})`}</option>
                          ))}
                        </select>
                        <input
                          name="whatsapp_number"
                          value={form.whatsapp_number}
                          onChange={handleChange}
                          className="flex-1 border p-2 rounded-r"
                        />
                      </div>
                      {errors.whatsapp_number && (
                        <p className="text-red-500 text-sm">{errors.whatsapp_number}</p>
                      )}
                    </>
                  )}

                  {/* Section 2: Business Details */}
                  {idx === 2 && (
                    <>
                      <div>
                        <label className="block font-medium">Service Description</label>
                        <textarea
                          name="service_description"
                          value={form.service_description}
                          onChange={handleChange}
                          className="mt-1 w-full border p-2 rounded"
                          placeholder="Enter Different services provided by you separated by Comma ','"
                          rows={3}
                        />
                        {errors.service_description && (
                          <p className="text-red-500 text-sm">{errors.service_description}</p>
                        )}
                      </div>
                      <div>
                        <label className="block font-medium">State</label>
                        <select
                          name="location_filter_state"
                          value={form.location_filter_state}
                          onChange={handleChange}
                          className="mt-1 w-full border p-2 rounded"
                        >
                          <option value="">Select state</option>
                          {statesList.map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                        {errors.location_filter_state && (
                          <p className="text-red-500 text-sm">{errors.location_filter_state}</p>
                        )}
                      </div>
                      <div>
                        <label className="block font-medium">Locations Served</label>
                        <div className="flex flex-wrap gap-2">
                          {locationsList
                            .filter(l => l.state === form.location_filter_state)
                            .map(l => (
                              <label key={l.name} className="inline-flex items-center bg-gray-100 px-3 py-1 rounded cursor-pointer">
                                <input
                                  type="checkbox"
                                  value={l.name}
                                  checked={form.service_providing_locations.includes(l.name)}
                                  onChange={handleLocationCheckboxChange}
                                  className="form-checkbox mr-2"
                                />
                                {l.name}
                              </label>
                            ))}
                        </div>
                        {errors.service_providing_locations && (
                          <p className="text-red-500 text-sm">{errors.service_providing_locations}</p>
                        )}
                        {form.service_providing_locations.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {form.service_providing_locations.map(loc => (
                              <span key={loc} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                                {loc}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Section 3: Address */}
                  {idx === 3 && (
                    <>
                      <div>
                        <label className="block font-medium">Address Line 1</label>
                        <input
                          name="address_line1"
                          value={form.address_line1}
                          onChange={handleChange}
                          className="mt-1 w-full border p-2 rounded"
                        />
                        {errors.address_line1 && (
                          <p className="text-red-500 text-sm">{errors.address_line1}</p>
                        )}
                      </div>
                      <div>
                        <label className="block font-medium">Address Line 2</label>
                        <input
                          name="address_line2"
                          value={form.address_line2}
                          onChange={handleChange}
                          className="mt-1 w-full border p-2 rounded"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <input
                          name="city"
                          value={form.city}
                          onChange={handleChange}
                          className="border p-2 rounded"
                          placeholder="City"
                        />
                        <input
                          name="state"
                          value={form.state}
                          onChange={handleChange}
                          className="border p-2 rounded"
                          placeholder="State"
                        />
                        <input
                          name="zip_code"
                          value={form.zip_code}
                          onChange={handleChange}
                          className="border p-2 rounded"
                          placeholder="ZIP"
                        />
                      </div>
                      <div>
                        <label className="block font-medium">Country</label>
                        <input
                          name="country"
                          value={form.country}
                          onChange={handleChange}
                          className="mt-1 w-full border p-2 rounded"
                        />
                        {errors.country && (
                          <p className="text-red-500 text-sm">{errors.country}</p>
                        )}
                      </div>
                    </>
                  )}                 

                  {/* Section 4: Additional Details */}
{idx === 4 && (
  <>
    <div>
      <label className="block font-medium">About Us</label>
      <textarea
        name="about_us"
        value={form.about_us}
        onChange={handleChange}
        className="mt-1 w-full border p-2 rounded"
        rows={4}
        
      />
<AIAboutGenerator
  category={form.service_categories}
  location={form.location_filter_state}
  business_name={form.business_name}
  onGenerate={(content) =>
    setForm({ ...form, about_us: content })
  }
/>
      {errors.about_us && (
        <p className="text-red-500 text-sm">{errors.about_us}</p>
      )}
      
    </div>

    <div>
      <label className="block font-medium">Website URL</label>
      <input
        name="website_url"
        value={form.website_url}
        onChange={handleChange}
        className="mt-1 w-full border p-2 rounded"
      />
      {errors.website_url && (
        <p className="text-red-500 text-sm">{errors.website_url}</p>
      )}
    </div>

    <div>
      <label className="block font-medium">Facebook Link</label>
      <input
        name="facebook_link"
        value={form.facebook_link}
        onChange={handleChange}
        className="mt-1 w-full border p-2 rounded"
      />
      {errors.facebook_link && (
        <p className="text-red-500 text-sm">{errors.facebook_link}</p>
      )}
    </div>

    <div>
      <label className="block font-medium">Instagram Link</label>
      <input
        name="instagram_link"
        value={form.instagram_link}
        onChange={handleChange}
        className="mt-1 w-full border p-2 rounded"
      />
      {errors.instagram_link && (
        <p className="text-red-500 text-sm">{errors.instagram_link}</p>
      )}
    </div>

    <div>
      <label className="block font-medium">Portfolio Link</label>
      <input
        name="portfolio_link"
        value={form.portfolio_link}
        onChange={handleChange}
        className="mt-1 w-full border p-2 rounded"
      />
      {errors.portfolio_link && (
        <p className="text-red-500 text-sm">{errors.portfolio_link}</p>
      )}
    </div>

    <div>
      <label className="block font-medium">Languages Spoken</label>
      <input
        name="languages_spoken"
        value={form.languages_spoken}
        onChange={handleChange}
        className="mt-1 w-full border p-2 rounded"
      />
    </div>

    <div className="grid grid-cols-2 gap-2">
      <input
        name="hourly_rate"
        value={form.hourly_rate}
        onChange={handleChange}
        className="border p-2 rounded"
        placeholder="Hourly Rate"
      />
      <input
        name="flat_rate"
        value={form.flat_rate}
        onChange={handleChange}
        className="border p-2 rounded"
        placeholder="Flat Rate"
      />
    </div>

    <div>
      <label className="block font-medium">Availability Notes</label>
      <textarea
        name="availability_notes"
        value={form.availability_notes}
        onChange={handleChange}
        className="mt-1 w-full border p-2 rounded"
        rows={2}
      />
    </div>
  </>
)}
                  {/* Section 5: Upload Image */}
                  {idx === 5 && (
                    <div>
                      <label className="block font-medium">Display Image</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="mt-1"
                      />
                      {loading && <p>Uploading…</p>}
                      {form.display_image_url && (
                        <img
                          src={form.display_image_url}
                          alt="Preview"
                          className="mt-2 w-32 h-32 object-cover rounded border-2 border-orange-500 shadow-lg transform transition-transform duration-300 hover:scale-105"
                        />
                      )}
                      {errors.display_image_url && (
                        <p className="text-red-500 text-sm">{errors.display_image_url}</p>
                      )}
                    </div>
                  )}

                  {/* Section 6: Terms & Submit */}
                  {idx === 6 && (
                    <>
                      <div>
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            name="terms_accepted"
                            checked={form.terms_accepted}
                            onChange={handleChange}
                            className="form-checkbox"
                          />
                          <span className="ml-2">I accept the </span>
                          <button
                            type="button"
                            onClick={() => setShowTerms(true)}
                            className="ml-1 underline text-blue-600"
                          >
                            terms and conditions
                          </button>
                        </label>
                        {errors.terms_accepted && (
                          <p className="text-red-500 text-sm">{errors.terms_accepted}</p>
                        )}
                      </div>
                      <div className="flex justify-between">
                        {activeSection > 0 && (
                          <button
                            type="button"
                            onClick={handleBack}
                            className="px-4 py-2 bg-gray-300 rounded"
                          >
                            Back
                          </button>
                        )}
                        <button
                          type="submit"
                          disabled={loading}
                          className="px-4 py-2 bg-green-600 text-white rounded"
                        >
                          {loading ? 'Submitting…' : 'Submit'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Step indicator */}
          <div className="text-center text-gray-600">
            Step {activeSection + 1} of {sections.length}
          </div>
        </form>
      </div>

      {/* Category Info Modal */}
      {showCategoryInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-md p-6 relative">
            <button
              onClick={() => setShowCategoryInfo(false)}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
            <h3 className="text-xl font-semibold mb-4">Service Categories</h3>
            <div className="overflow-auto max-h-64">
              <table className="w-full text-left">
                <thead>
                  <tr>
                    <th className="py-2 border-b">Category</th>
                    <th className="py-2 border-b">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {categoriesList.map(cat => (
                    <tr key={cat.id}>
                      <td className="py-2">{cat.categories}</td>
                      <td className="py-2">{cat.Description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Terms & Conditions Modal */}
      {showTerms && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 overflow-auto max-h-[80vh]">
            <h3 className="text-xl font-bold mb-4">Terms and Conditions</h3>
            <div className="text-sm text-gray-700 whitespace-pre-wrap">
              {termsText}
            </div>
            <div className="text-right mt-4">
              <button
                onClick={() => setShowTerms(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}